import { useEffect, useRef, useState } from 'react';
import OktaSignIn from '@okta/okta-signin-widget';
import { withOktaAuth } from '@okta/okta-react';
import type {
  OktaAuth,
  AuthState,
  IdxTransactionManagerInterface,
} from '@okta/okta-auth-js';

import { useOkta } from 'utils/okta/use-okta';

import { Box } from 'ui/box';

import {
  enhanceLinkButtonRoles,
  enhanceAuthenticatorListSemantics,
  enhanceSendPushDisabledState,
  isIdxTransactionExpired,
} from 'containers/login/okta-widget/okta-sign-in-widget.helpers';
import { trackOktaWidgetError } from 'containers/login/okta-widget/analytics/okta-login-analytics';

import '@okta/okta-signin-widget/css/okta-sign-in.min.css';
import 'containers/login/okta-widget/okta-theme.css';
import { clearOktaTransaction } from 'utils/okta/okta-auth-errors';

interface OktaInjectedProps {
  oktaAuth: OktaAuth;
  authState: AuthState | null;
}

interface OktaAfterRenderContext {
  controller: string | null;
}

interface OktaErrorContext {
  name?: string;
  message?: string;
  statusCode?: number;
  xhr?: unknown;
}

// The classic Okta widget hard-codes its form title as <h2>, but on /login this
// element is visually the page's primary heading. Per WCAG 1.3.1 we render our
// own real <h1> for the primary-auth screen and hide the widget's <h2> via CSS
// (see okta-theme.css). Other screens (MFA, password reset, etc.) keep the
// widget's native title untouched.
const PRIMARY_AUTH_CONTROLLER = 'primary-auth';

const OktaSignInWidget = (_props: OktaInjectedProps): React.JSX.Element => {
  const oktaAuth = useOkta();
  const widgetRef = useRef<OktaSignIn | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeController, setActiveController] = useState<string | null>(null);
  const [restartKey, setRestartKey] = useState(0);

  useEffect(() => {
    const el = containerRef.current;

    if (!el) return undefined;

    const oktaOptions = oktaAuth.options as typeof oktaAuth.options & {
      url?: string;
    };

    widgetRef.current = new OktaSignIn({
      baseUrl: oktaOptions.url,
      clientId: oktaOptions.clientId,
      redirectUri: oktaOptions.redirectUri,
      authParams: {
        issuer: oktaOptions.issuer,
        responseType: ['code'],
        scopes: ['openid', 'profile', 'email', 'offline_access'],
      },
      features: {
        hideSignOutLinkInMFA: false,
      },
      helpLinks: {
        forgotPassword: '/account/request-password-reset',
      },
      brandName: 'MARKETview',
      logo: undefined,
      i18n: {
        en: {
          autoPush: 'Send push automatically next time',
          'oie.select.authenticators.enroll.subtitle.custom':
            '{0} requires multifactor authentication to add an additional layer of security when signing in to your account.',
        },
      },
    });

    const widget = widgetRef.current;
    const widgetAuthClient = widget.authClient;
    let currentController: string | null = null;

    const restartWidget = (): void => {
      clearOktaTransaction(widgetAuthClient);
      setRestartKey((key) => key + 1);
    };

    const handleAfterRender = (context: OktaAfterRenderContext): void => {
      currentController = context.controller;
      setActiveController(context.controller);

      if (containerRef.current) {
        enhanceLinkButtonRoles(containerRef.current);
        enhanceAuthenticatorListSemantics(containerRef.current);
        enhanceSendPushDisabledState(containerRef.current);
      }
    };

    const handleAfterError = (
      context: OktaAfterRenderContext,
      error?: OktaErrorContext
    ): void => {
      trackOktaWidgetError({
        controller: context.controller ?? 'unknown',
        errorName: error?.name ?? 'UnknownError',
        statusCode: error?.statusCode,
        message: error?.message ?? 'Unknown widget error',
      });
      console.error('Okta sign-in widget error:', {
        controller: context.controller,
        name: error?.name,
        statusCode: error?.statusCode,
        message: error?.message,
      });
    };

    const handleReturnToTab = (): void => {
      if (document.visibilityState !== 'visible') return;
      if (currentController !== PRIMARY_AUTH_CONTROLLER) return;

      if (
        isIdxTransactionExpired(
          widgetAuthClient?.transactionManager as IdxTransactionManagerInterface
        )
      ) {
        restartWidget();
      }
    };

    widget.on('afterRender', handleAfterRender);
    widget.on('afterError', handleAfterError);
    document.addEventListener('visibilitychange', handleReturnToTab);
    window.addEventListener('focus', handleReturnToTab);

    widget
      .showSignInAndRedirect({ el: el as unknown as string })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        const name = err instanceof Error ? err.name : 'UnknownError';

        trackOktaWidgetError({
          controller: 'unknown',
          errorName: name,
          message,
        });
        console.error('SignIn Widget Error:', { name, message });
      });

    return () => {
      document.removeEventListener('visibilitychange', handleReturnToTab);
      window.removeEventListener('focus', handleReturnToTab);
      widget.off('afterRender', handleAfterRender);
      widget.off('afterError', handleAfterError);
      widget.remove();
    };
  }, [oktaAuth, restartKey]);

  return (
    <>
      {activeController === PRIMARY_AUTH_CONTROLLER && (
        <h1 className="text-lg tablet:text-2xl font-semibold text-navy-800 text-center mb-6">
          Log In
        </h1>
      )}
      <Box ref={containerRef} />
    </>
  );
};

export default withOktaAuth(OktaSignInWidget);
